import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useQuery } from '@apollo/react-hooks';
import { connect } from 'react-redux';
import { change, Field, FieldArray, reduxForm } from 'redux-form';

import { BOOK_DATES } from 'graphql/queries/accommodations';
import { ACTIVITIES_AVAILABLE_PARTICIPANTS } from 'graphql/queries/activities';

import StartTimeAndDurationRadioButtonsGroup from 'pages/book-activity/components/start-time-and-duration-radio-buttons-group/StartTimeAndDurationRadioButtonsGroup';

import { getListOfAvailableCurrencies } from 'utils/book-accommodation-and-activity/currency';
import { getParticipants as getAccommodationParticipants } from 'utils/book-accommodation/prepareParticipants';
import { getParticipants as getActivityParticipants } from 'utils/book-activity/prepareParticipants';
import {
  getAvailableRangeDates as getAccommodationAvailableRangeDates,
  getDisabledDates as getAccommodationDisabledDates
} from 'utils/book-accommodation/availableDates';
import {
  getAvailableRangeDates as getActivityAvailableRangeDates,
  getDefaultStartTime,
  getDisabledDates as getActivityDisabledDates,
  getListOfStartTimeAndDuration,
  getListOfStartTimeAndDurationByCutoffPeriod
} from 'utils/book-activity/availableDates';
import {
  getAvailableParticipantsForStartTime,
  getMaxParticipants,
  getMaxParticipantsActivitySeveralDays
} from 'utils/book-activity/maxParticipants';
import { getReceivedActivityBookingsDates } from 'utils/accommodations-and-activities/prepareReceivedActivityData';

import { CustomDateRangePicker, MultiDatePickerCalendar, SingleDatePicker } from 'components';

import BookingInformation from '../booking-information/BookingInformation';
import Visitors from '../visiters/Visiters';

import validate from './validate';

import useStyles from './styles';
import WidgetLoader from '../widget-loader/WidgetLoader';

const initialValues = {
  startBookingsDate: moment(),
  dates: null,
  startingDate: null,
  participants: [],
  currency: null,
  availableCurrencies: [],
  maxParticipants: 0,
  addOns: [],
  mandatoryFees: [],
  activities: [],
  languages: [],
  selectedLanguage: 'English',
  minNights: 1
};

const SCHEDULE_DATE_AND_TIME_NAME = 'scheduleDateTimeForm';
const SCHEDULE_PASS_NAME = 'schedulePassForm';
const SCHEDULE_DATE_NO_TIME_NAME = 'scheduleDateForm';

const DateAndGuests = ({
  isAccommodationBooking,
  initialSelectedCurrency,
  initialSelectedLanguage,
  productData,
  receivedProduct,
  changeFormFieldValue,
  widgetForm,
  handleSubmit,
  ownerId,
  languages,
  activityDisabledDates
}) => {
  const classes = useStyles();
  const productId = productData.generalForm.id;
  const scheduleType = productData.scheduleType;

  const dates = widgetForm.values.dates;
  const startDate = dates && dates.startDate;

  const [listStartTimeAndDuration, setListStartTimeAndDuration] = useState([]);
  const [isUpdate, setIsUpdate] = useState(false);
  const [calendarFirstDate, setCalendarFirstDate] = useState(null);
  const [availableParticipantsData, setAvailableParticipantsData] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (widgetForm.values.dates?.startDate && widgetForm.values.dates?.endDate) {
      setTimeout(() => {
        bottomRef.current.scrollIntoView({ behavior: "smooth" })
      }, 0)
    }
  }, [widgetForm.values.dates])

  useEffect(() => {
    if (widgetForm?.submitFailed && widgetForm?.syncErrors?.participants?._error) {
      setTimeout(() => {
        bottomRef.current.scrollIntoView({ behavior: "smooth" })
      }, 0)
    }
  }, [widgetForm?.syncErrors?.participants?._error, widgetForm?.submitFailed])

  useEffect(() => {
    if (!widgetForm.values.languages.length) {
      changeFormFieldValue('languages', languages);
      changeFormFieldValue('selectedLanguage', initialSelectedLanguage);
    }

    setCalendarFirstDate(
      widgetForm.values.startingDate || startDate || moment()
    );
    const maxParticipants = productData['generalForm'].maxParticipants;
    if (maxParticipants) {
      setMaxParticipants(maxParticipants);
    }
    changeFormFieldValue('minNights', productData['generalForm'].minNights);
  }, []);

  useLayoutEffect(() => {
    const updateSize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const { loading: bookDatesLoading, data: bookDatesData } = useQuery(
    BOOK_DATES,
    {
      skip: !isAccommodationBooking || !calendarFirstDate,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      variables: {
        businessOwnerId: ownerId,
        accommodationId: productId,
        startPeriod: moment(calendarFirstDate)
          .startOf('month')
          .format('YYYY-MM-DD'),
        endPeriod: moment(moment(calendarFirstDate).add(1, 'months'))
          .endOf('month')
          .format('YYYY-MM-DD')
      }
    }
  );

  const {
    loading: availableParticipantsLoading,
    data: receivedAvailableParticipantsData
  } = useQuery(ACTIVITIES_AVAILABLE_PARTICIPANTS, {
    skip: isAccommodationBooking || !calendarFirstDate,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      businessOwnerId: ownerId,
      activityId: productId,
      startPeriod: moment(calendarFirstDate)
        .startOf('month')
        .format('YYYY-MM-DD'),
      endPeriod: moment(moment(calendarFirstDate).add(1, 'months'))
        .endOf('month')
        .format('YYYY-MM-DD'),
      availableParticipantsData: availableParticipantsData
    }
  });

  useEffect(() => {
    if (!receivedAvailableParticipantsData) return;

    const availableParticipantsData = getReceivedActivityBookingsDates(
      receivedAvailableParticipantsData
    );

    setAvailableParticipantsData(availableParticipantsData);
    changeFormFieldValue(
      'availableParticipantsData',
      availableParticipantsData.activities[0].availableParticipantsPerDate
    );
  }, [receivedAvailableParticipantsData]);

  useEffect(() => {
    if (!receivedProduct) return;

    setListOfAvailableCurrencies(receivedProduct);
  }, [receivedProduct]);

  useEffect(() => {
    if (!isAccommodationBooking) {
      if (productData.scheduleType === SCHEDULE_PASS_NAME) {
        setParticipants(productData);
      }
      return;
    }

    const availableCurrencies = widgetForm.values.availableCurrencies;
    if (!availableCurrencies.length) return;

    const guests = productData.generalForm.guests;
    changeFormFieldValue('maxParticipants', guests);

    setParticipants(productData);
  }, [widgetForm.values.availableCurrencies, widgetForm.values.dates]);

  useEffect(() => {
    setParticipantsAndStartTimes();
  }, [widgetForm.values.startingDate, availableParticipantsData, scheduleType]);

  useEffect(() => {
    if (!widgetForm.values.multiDates) return;
    let { startDate, endDate } = widgetForm.values.multiDates;
    if (!endDate) {
      endDate = startDate;
    }
    const isSame = moment(startDate).isSame(endDate, 'day');
    if (isSame) {
      changeFormFieldValue('dates', null);
      changeFormFieldValue('startingDate', startDate);
    } else {
      changeFormFieldValue('startingDate', null);
      changeFormFieldValue('dates', widgetForm.values.multiDates);
      handleMultiDateAvailableSeats(productData);
      setParticipants(productData);
    }
  }, [widgetForm.values.multiDates]);

  const setParticipantsAndStartTimes = () => {
    const startingDate = widgetForm.values.startingDate;
    if (!startingDate) return;

    const startPeriod = moment(calendarFirstDate).startOf('month');
    const endPeriod = moment(moment(calendarFirstDate).add(1, 'months')).endOf(
      'month'
    );

    if (moment(startingDate).isBetween(startPeriod, endPeriod, 'day', '[]')) {
      setParticipants(productData);
      handleListStartTimeAndDurationField(productData);
    }
  };

  const setListOfAvailableCurrencies = receivedProduct => {
    const listOfAvailableCurrencies = getListOfAvailableCurrencies(
      receivedProduct.prices
    );

    changeFormFieldValue('availableCurrencies', listOfAvailableCurrencies);
    changeFormFieldValue('currency', initialSelectedCurrency);
  };

  const setParticipants = accommodation => {
    const currency = widgetForm.values.currency;
    const dates = widgetForm.values.dates;
    const createdParticipants = widgetForm.values.participants;

    let participants = [];

    if (isAccommodationBooking) {
      // if (createdParticipants && createdParticipants.length) return;
      if (dates && dates.startDate && dates.endDate) {
        participants = getAccommodationParticipants(
          accommodation,
          currency,
          createdParticipants,
          dates
        );
      } else {
        participants = [];
      }
    } else {
      participants = getActivityParticipants(
        accommodation,
        widgetForm,
        isUpdate => setIsUpdate(isUpdate),
        isUpdate,
        currency,
        createdParticipants
      );
    }

    changeFormFieldValue('participants', participants);
  };

  const handleMultiDateAvailableSeats = activity => {
    if (!activity || !widgetForm.values.multiDates) return;

    let { startDate, endDate } = widgetForm.values.multiDates;

    if (!startDate) return;

    if (!endDate) {
      endDate = startDate;
    }

    const availableParticipantsPerDate = availableParticipantsData
      ? availableParticipantsData.activities[0].availableParticipantsPerDate
      : [];

    const availableParticipants = getMaxParticipantsActivitySeveralDays(
      availableParticipantsPerDate,
      startDate,
      endDate
    );

    if (availableParticipants !== null) {
      setMaxParticipants(availableParticipants);
    } else {
      setMaxParticipants(activity.generalForm.maxParticipants);
    }
  };

  const handleListStartTimeAndDurationField = activity => {
    if (!activity) return;

    const startingDate = widgetForm.values.startingDate;
    const availableParticipantsPerDate = availableParticipantsData
      ? availableParticipantsData.activities[0].availableParticipantsPerDate
      : [];
    let listOfStartTimeAndDuration = getListOfStartTimeAndDuration(
      availableParticipantsPerDate,
      activity,
      widgetForm
    );
    listOfStartTimeAndDuration = getListOfStartTimeAndDurationByCutoffPeriod(
      activity,
      listOfStartTimeAndDuration,
      startingDate
    );
    let selectedStartTime = getDefaultStartTime(
      listOfStartTimeAndDuration,
      widgetForm
    );

    if (!selectedStartTime) {
      setMaxParticipants(activity.generalForm.maxParticipants);
      availableParticipantsPerDate.map(item => {
        const formattedStartDate = startingDate
          ? moment(startingDate).format('YYYY-MM-DD')
          : '';
        if (!item.startTime && item.date === formattedStartDate) {
          setMaxParticipants(item.availableParticipants);
        }
      });
    } else {
      setMaxParticipants(selectedStartTime.seatsLeft);

      listOfStartTimeAndDuration = getAvailableParticipantsForStartTime(
        listOfStartTimeAndDuration,
        null,
        widgetForm,
        availableParticipantsData
      );
    }

    selectedStartTime = getDefaultStartTime(
      listOfStartTimeAndDuration,
      widgetForm
    );

    changeFormFieldValue('startTime', selectedStartTime);
    setListStartTimeAndDuration(listOfStartTimeAndDuration);
  };

  const setMaxParticipants = seatsLeft => {
    const maxParticipants = getMaxParticipants(
      seatsLeft,
      null,
      widgetForm,
      availableParticipantsData
    );

    changeFormFieldValue('maxParticipants', maxParticipants);
  };

  return (
    <>
      <WidgetLoader loading={bookDatesLoading || availableParticipantsLoading} />

      {scheduleType === SCHEDULE_DATE_NO_TIME_NAME ? (
        <div className={classes.wrapper}>
          <div className={classes.leftSide}>
            <form onSubmit={handleSubmit}>
              {isAccommodationBooking ? (
                <Field
                  name="dates"
                  availableRange={getAccommodationAvailableRangeDates(
                    productData
                  )}
                  disabledDates={getAccommodationDisabledDates(bookDatesData)}
                  changeMonthsCallback={setCalendarFirstDate}
                  className={classes.dateRange}
                  inputClassName={classes.disableInputFocus}
                  label="Select dates"
                  initialFocus="startDate"
                  dateRangePickerClass="dateRangeOpenedPicker"
                  numberOfMonths={windowWidth > 700 ? 2 : 1}
                  isHideDatesLabel={windowWidth <= 700}
                  daySize={windowWidth > 600 ? 35 : 41}
                  labelTypographyVariant={'h3'}
                  secondLabelClass={classes.label}
                  component={CustomDateRangePicker}
                  widget
                />
              ) : (
                <>
                  {scheduleType !== SCHEDULE_PASS_NAME && (
                    <Field
                      widget
                      name="multiDates"
                      initialFocus="startDate"
                      label="Starting date"
                      dateRangePickerClass="dateRangeOpenedPicker"
                      availableRange={getActivityAvailableRangeDates(productData)}
                      disabledDates={getActivityDisabledDates(
                        availableParticipantsData,
                        null,
                        productData
                      ).concat(activityDisabledDates)}
                      changeMonthsCallback={setCalendarFirstDate}
                      className={classes.dateRange}
                      inputClassName={classes.disableInputFocus}
                      secondLabelClass={classes.label}
                      numberOfMonths={windowWidth > 700 ? 2 : 1}
                      component={MultiDatePickerCalendar}
                      daySize={windowWidth > 600 ? 35 : 41}
                      labelTypographyVariant={'h3'}
                      labelClass={classes.label}
                      cutoffData={productData[productData.scheduleType]}
                    />
                  )}
                </>
              )}

              {scheduleType === SCHEDULE_DATE_AND_TIME_NAME && (
                <Field
                  name="startTime"
                  label="Start time & Duration"
                  list={listStartTimeAndDuration}
                  className={classes.startTimeWrapper}
                  labelClass={classes.startTimeLabel}
                  startTimeClass={classes.startTimeClass}
                  component={StartTimeAndDurationRadioButtonsGroup}
                />
              )}
              <FieldArray
                name="participants"
                title="Select number of guests"
                component={Visitors}
                props={{ widgetForm }}
                titleClass={classes.label}
                itemClass={classes.participant}
                currency={widgetForm.values.currency || 'NOK'}
              />
              <div ref={bottomRef} />
              <button hidden={true} type={'submit'}>
                Submit
              </button>
            </form>
          </div>

          <div className={classes.rightSide}>
            <BookingInformation data={productData} form={widgetForm} />
          </div>
        </div>
      ) : (
        <div className={classes.wrapper}>
          <div className={classes.leftSide}>
            <form onSubmit={handleSubmit}>
              {isAccommodationBooking ? (
                <Field
                  name="dates"
                  availableRange={getAccommodationAvailableRangeDates(
                    productData
                  )}
                  disabledDates={getAccommodationDisabledDates(bookDatesData)}
                  changeMonthsCallback={setCalendarFirstDate}
                  className={classes.dateRange}
                  inputClassName={classes.disableInputFocus}
                  label="Select dates"
                  initialFocus="startDate"
                  dateRangePickerClass="dateRangeOpenedPicker"
                  numberOfMonths={windowWidth > 700 ? 2 : 1}
                  isHideDatesLabel={windowWidth <= 700}
                  daySize={windowWidth > 600 ? 35 : 41}
                  labelTypographyVariant={'h3'}
                  secondLabelClass={classes.label}
                  component={CustomDateRangePicker}
                  widget
                />
              ) : (
                <>
                  {scheduleType !== SCHEDULE_PASS_NAME && (
                    <Field
                      name="startingDate"
                      label="Starting date"
                      availableRange={getActivityAvailableRangeDates(productData)}
                      disabledDates={getActivityDisabledDates(
                        availableParticipantsData,
                        null,
                        productData
                      )}
                      changeMonthsCallback={setCalendarFirstDate}
                      className={classes.dateRange}
                      numberOfMonths={windowWidth > 700 ? 2 : 1}
                      component={SingleDatePicker}
                      daySize={windowWidth > 600 ? 35 : 41}
                      labelTypographyVariant={'h3'}
                      labelClass={classes.label}
                      cutoffData={productData[productData.scheduleType]}
                    />
                  )}
                </>
              )}

              {scheduleType === SCHEDULE_DATE_AND_TIME_NAME && (
                <Field
                  name="startTime"
                  label="Start time & Duration"
                  list={listStartTimeAndDuration}
                  className={classes.startTimeWrapper}
                  labelClass={classes.startTimeLabel}
                  startTimeClass={classes.startTimeClass}
                  component={StartTimeAndDurationRadioButtonsGroup}
                />
              )}
              <FieldArray
                name="participants"
                title="Select number of guests"
                component={Visitors}
                props={{ widgetForm }}
                titleClass={classes.label}
                itemClass={classes.participant}
                currency={widgetForm.values.currency || 'NOK'}
              />
              <div ref={bottomRef} />
              <button hidden={true} type={'submit'}>
                Submit
              </button>
            </form>
          </div>

          <div className={classes.rightSide}>
            <BookingInformation data={productData} form={widgetForm} />
          </div>
        </div>
      )}
    </>
  )
};

const mapStateToProps = state => {
  return {
    widgetForm: state.form.widgetForm
  };
};

const mapDispatchToProps = dispatch => ({
  changeFormFieldValue: (field, value) => {
    dispatch(change('widgetForm', field, value));
  }
});

export default reduxForm({
  form: 'widgetForm',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  touchOnBlur: false,
  validate,
  initialValues
})(connect(mapStateToProps, mapDispatchToProps)(DateAndGuests));
